import React, { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  IdentificationBadge, ShieldCheck, Upload, Lock, CheckCircle, Warning, FileText
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { motion } from "framer-motion";
import { toast } from "sonner";

export default function IdentityAnchor({ onVerified }) {
  const [status, setStatus] = useState(null);
  const [ein, setEin] = useState("");
  const [legalName, setLegalName] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/identity/status");
      setStatus(data);
      if (data.identity_verified && onVerified) onVerified(data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { loadStatus(); }, []);

  const formatEinInput = (v) => {
    const digits = v.replace(/\D/g, "").slice(0, 9);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!file) { setErr("Upload your government-issued ID."); return; }
    if (file.size > 5 * 1024 * 1024) { setErr("File exceeds 5MB limit."); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("ein", ein);
      fd.append("legal_name", legalName);
      fd.append("id_document", file);
      const { data } = await api.post("/identity/submit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setStatus({ ...status, ...data, id_doc_name: file.name });
      toast.success("Identity verified, Commander. Stage 2 unlocked.");
      if (onVerified) onVerified(data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  // VERIFIED STATE
  if (status?.identity_verified) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="surface-card armor-border p-6 md:p-8 relative overflow-hidden"
        data-testid="identity-anchor-verified"
      >
        <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-[#FFD700]/15 blur-3xl" />
        <div className="relative z-10 flex items-start gap-5">
          <div className="w-14 h-14 border border-[#FFD700] bg-[#FFD700]/10 flex items-center justify-center flex-shrink-0">
            <ShieldCheck size={28} weight="duotone" className="text-[#FFD700]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="overline mb-1">// Identity Anchor // Verified</div>
            <h3 className="text-xl font-bold text-white mb-3">Sovereign Identity Locked</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <div className="overline text-[0.55rem] mb-1">Legal Operator</div>
                <div className="text-sm text-white">{status.legal_name}</div>
              </div>
              <div>
                <div className="overline text-[0.55rem] mb-1">EIN / Tax ID</div>
                <div className="font-mono text-sm text-[#FFD700]">{status.ein}</div>
              </div>
              <div className="sm:col-span-2">
                <div className="overline text-[0.55rem] mb-1">Document on File</div>
                <div className="flex items-center gap-2 text-xs text-white/70 font-mono">
                  <FileText size={14} /> {status.id_doc_name}
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 text-xs text-[#FFD700]">
              <CheckCircle weight="fill" size={14} />
              <span className="uppercase tracking-widest font-semibold">Stage 2 & 3 access granted</span>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // UNVERIFIED — submission form
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="surface-card armor-border p-6 md:p-8 border-[#FFD700]/30"
      data-testid="identity-anchor-unverified"
    >
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 border border-[#FFD700] flex items-center justify-center flex-shrink-0 animate-pulse-gold">
          <IdentificationBadge size={24} weight="duotone" className="text-[#FFD700]" />
        </div>
        <div>
          <div className="overline mb-1">// Identity Anchor // Required</div>
          <h3 className="text-xl font-bold text-white mb-1">Verify your Sovereign Identity</h3>
          <p className="text-sm text-white/70 font-light">
            Director's voice: "Commander, before we deploy the Ghost Crew, I need your legal hull
            locked. Submit your government ID and EIN — this is a hard-stop. Stage 2 stays sealed
            until this anchor is set."
          </p>
        </div>
      </div>

      <div className="mb-5 flex items-start gap-3 p-3 border border-[#FFD700]/20 bg-[#FFD700]/5">
        <Warning size={16} weight="duotone" className="text-[#FFD700] flex-shrink-0 mt-0.5" />
        <p className="text-xs text-white/70 font-light">
          Documents are hashed (SHA-256) and never stored as raw files. Your EIN is encrypted at rest.
          This anchor is required by sovereign trust protocol.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-5">
        <div>
          <Label className="overline text-[0.6rem] mb-2 block">Legal Operator Name / Business Name</Label>
          <Input
            data-testid="identity-legal-name"
            value={legalName}
            onChange={(e) => setLegalName(e.target.value)}
            placeholder="John Sovereign Doe / Sovereign Ventures LLC"
            required
            className="bg-[#0c0c0c] border-white/10 rounded-none h-12 text-white"
          />
        </div>

        <div>
          <Label className="overline text-[0.6rem] mb-2 block">EIN (Employer Identification Number)</Label>
          <Input
            data-testid="identity-ein"
            value={ein}
            onChange={(e) => setEin(formatEinInput(e.target.value))}
            placeholder="XX-XXXXXXX"
            required
            className="font-mono bg-[#0c0c0c] border-white/10 rounded-none h-12 text-white tracking-widest"
          />
          <div className="text-[0.65rem] font-mono uppercase tracking-widest text-white/40 mt-2">
            Get one free at <a href="https://www.irs.gov/businesses/small-businesses-self-employed/apply-for-an-employer-identification-number-ein-online" target="_blank" rel="noopener noreferrer" className="text-[#FFD700] hover:underline">IRS.gov</a>
          </div>
        </div>

        <div>
          <Label className="overline text-[0.6rem] mb-2 block">Government ID Document</Label>
          <label
            htmlFor="id-doc-upload"
            data-testid="identity-doc-upload-label"
            className="flex items-center justify-center gap-3 border border-dashed border-white/20 hover:border-[#FFD700]/50 bg-[#0c0c0c] py-8 cursor-pointer transition-all group"
          >
            <Upload size={24} weight="duotone" className="text-[#FFD700] group-hover:scale-110 transition-transform" />
            <div className="text-left">
              <div className="text-sm font-semibold text-white">
                {file ? file.name : "Click to upload ID"}
              </div>
              <div className="text-[0.65rem] font-mono uppercase tracking-widest text-white/40 mt-0.5">
                {file ? `${(file.size / 1024).toFixed(0)} KB` : "JPG / PNG / WEBP / PDF — Max 5MB"}
              </div>
            </div>
          </label>
          <input
            id="id-doc-upload"
            data-testid="identity-doc-upload-input"
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="hidden"
          />
        </div>

        {err && (
          <div data-testid="identity-error" className="text-sm text-red-400 border border-red-900/40 bg-red-950/30 p-3 font-mono text-xs">
            {err}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !ein || !legalName || !file}
          data-testid="identity-submit-btn"
          className="w-full btn-gold py-4 text-xs flex items-center justify-center gap-3 disabled:opacity-50"
        >
          {submitting ? (
            <>Anchoring identity...</>
          ) : (
            <>
              <Lock size={14} weight="bold" />
              Lock Identity Anchor
            </>
          )}
        </button>
      </form>
    </motion.div>
  );
}
